import {
  Breadcrumbs,
  Button,
  Chip,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IoCarSportSharp } from "react-icons/io5";
import { GoDashboard } from "react-icons/go";
import {
  MdOutlineTextFields,
  MdPrecisionManufacturing,
  MdReceipt,
  MdSubscriptions,
} from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SubscriptionPurchasesChart from "../../Components/Charts/SubscriptionPurchasesChart";
import { imageInstance, instance } from "../../Const/ApiHeader";
import {
  ALL_MANUFACTURER,
  ALL_SUBSCRIPTION,
  ALL_VEHICLE,
  resolveImageUrl,
  USER_SUBSCRIPTION_RECORDS,
} from "../../Const/ApiConst";

const isDiscontinued = (status) =>
  status === "Discontinued" || status === "Discountinued";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const QUICK_ACTIONS = [
  {
    label: "Add Vehicle",
    description: "Create a new listing",
    path: "/add_vehicles",
    icon: <AddIcon />,
  },
  {
    label: "Vehicles",
    description: "Browse the catalogue",
    path: "/vehicles",
    icon: <IoCarSportSharp />,
  },
  {
    label: "Manufacturers",
    description: "Manage brand logos",
    path: "/manufacturers",
    icon: <MdPrecisionManufacturing />,
  },
  {
    label: "Running Text",
    description: "App home banner",
    path: "/running-text",
    icon: <MdOutlineTextFields />,
  },
  {
    label: "Subscription Plans",
    description: "Pricing & validity",
    path: "/subscriptions",
    icon: <MdSubscriptions />,
  },
  {
    label: "Payment Records",
    description: "User subscriptions",
    path: "/subscription-records",
    icon: <MdReceipt />,
  },
];

function DashboardStatSkeleton() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="vehicles-stat-card">
          <Skeleton variant="text" width="40%" height={36} />
          <Skeleton variant="text" width="70%" height={20} />
        </div>
      ))}
    </>
  );
}

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function Home() {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleStats, setVehicleStats] = useState({
    total: 0,
    active: 0,
    discontinued: 0,
  });
  const [manufacturers, setManufacturers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [records, setRecords] = useState([]);
  const [chartDays, setChartDays] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
  });

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          instance.get(ALL_VEHICLE, {
            params: { page: 1, limit: 5, sort: "lastUpdated" },
          }),
          imageInstance.post(ALL_MANUFACTURER),
          instance.get(ALL_SUBSCRIPTION),
          instance.get(USER_SUBSCRIPTION_RECORDS, {
            params: { page: 1, limit: 5, includeChart: 1 },
          }),
        ]);

        const [vehicleRes, manufacturerRes, planRes, recordRes] = results;

        if (vehicleRes.status === "fulfilled") {
          const payload = vehicleRes.value?.data || {};
          const list = asList(payload);
          setVehicles(list);
          if (payload.stats) {
            setVehicleStats(payload.stats);
          } else {
            const discontinued = list.filter((v) => isDiscontinued(v.status)).length;
            setVehicleStats({
              total: list.length,
              active: list.length - discontinued,
              discontinued,
            });
          }
        }
        if (manufacturerRes.status === "fulfilled") {
          setManufacturers(asList(manufacturerRes.value?.data));
        }
        if (planRes.status === "fulfilled") {
          setPlans(asList(planRes.value?.data));
        }
        if (recordRes.status === "fulfilled") {
          const payload = recordRes.value?.data || {};
          setRecords(asList(payload));
          setChartDays(Array.isArray(payload.chartDays) ? payload.chartDays : []);
          if (payload.stats) {
            setSubscriptionStats({
              total: payload.stats.total || 0,
              active: payload.stats.active || 0,
              expired: payload.stats.expired || 0,
            });
          } else {
            const list = asList(payload);
            const active = list.filter((r) => r.isLive).length;
            setSubscriptionStats({
              total: list.length,
              active,
              expired: list.length - active,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const activeRate = subscriptionStats.total
    ? Math.round((subscriptionStats.active / subscriptionStats.total) * 100)
    : 0;

  const recentRecords = records.slice(0, 5);

  const recentVehicles = vehicles;

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="vehicles-page dashboard-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Dashboard</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero dash-hero">
        <div className="dash-hero-main">
          <Typography className="vehicles-hero-eyebrow">
            <GoDashboard fontSize="inherit" /> Admin overview
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            {getGreeting()}, Admin
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Your catalogue, brands, and subscriptions at a glance.
          </Typography>
          <div className="dash-hero-chips">
            <span className="dash-hero-chip">{todayLabel}</span>
            <span className="dash-hero-chip">
              {loading ? "—" : `${vehicleStats.active} active vehicles`}
            </span>
            <span className="dash-hero-chip">
              {loading ? "—" : `${subscriptionStats.active} live subscriptions`}
            </span>
          </div>
        </div>
        <div className="dash-hero-actions">
          <Link to="/add_vehicles">
            <Button variant="contained" startIcon={<AddIcon />}>
              Add Vehicle
            </Button>
          </Link>
          <Link to="/vehicles">
            <Button
              variant="outlined"
              startIcon={<DirectionsCarFilledOutlinedIcon />}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.45)" }}
            >
              View catalogue
            </Button>
          </Link>
        </div>
      </section>

      <section className="vehicles-stats-grid">
        {loading ? (
          <DashboardStatSkeleton />
        ) : (
          <>
            <Link to="/vehicles" className="dash-stat-link">
              <div className="vehicles-stat-card tone-blue">
                <Typography className="vehicles-stat-value">
                  {vehicleStats.total}
                </Typography>
                <Typography className="vehicles-stat-label">Total vehicles</Typography>
                <Typography className="dash-stat-footnote">
                  {vehicleStats.active} active · {vehicleStats.discontinued} discontinued
                </Typography>
              </div>
            </Link>
            <Link to="/manufacturers" className="dash-stat-link">
              <div className="vehicles-stat-card">
                <Typography className="vehicles-stat-value">
                  {manufacturers.length}
                </Typography>
                <Typography className="vehicles-stat-label">Manufacturers</Typography>
                <Typography className="dash-stat-footnote">Brands in catalogue</Typography>
              </div>
            </Link>
            <Link to="/subscriptions" className="dash-stat-link">
              <div className="vehicles-stat-card tone-muted">
                <Typography className="vehicles-stat-value">{plans.length}</Typography>
                <Typography className="vehicles-stat-label">Subscription plans</Typography>
                <Typography className="dash-stat-footnote">Available for purchase</Typography>
              </div>
            </Link>
            <Link to="/subscription-records" className="dash-stat-link">
              <div className="vehicles-stat-card tone-green">
                <Typography className="vehicles-stat-value">
                  {subscriptionStats.active}
                </Typography>
                <Typography className="vehicles-stat-label">Active subscriptions</Typography>
                <Typography className="dash-stat-footnote">
                  {subscriptionStats.expired} expired overall
                </Typography>
              </div>
            </Link>
          </>
        )}
      </section>

      <section className="section dash-card dash-chart-card dash-chart-fullwidth">
        <div className="dash-card-header">
          <div>
            <Typography className="dash-card-title">
              <ShowChartIcon fontSize="inherit" className="dash-card-title-icon" />
              Subscription purchases
            </Typography>
            <Typography className="dash-card-subtitle">
              Purchase volume by day, month, or year
            </Typography>
          </div>
          <Link to="/subscription-records">
            <Button size="small" endIcon={<ArrowForwardIcon />}>
              All records
            </Button>
          </Link>
        </div>
        <SubscriptionPurchasesChart
          records={records}
          chartDays={chartDays}
          loading={loading}
        />
      </section>

      <div className="dash-grid">
        <section className="section dash-card">
          <div className="dash-card-header">
            <div>
              <Typography className="dash-card-title">Quick actions</Typography>
              <Typography className="dash-card-subtitle">
                Jump straight to common admin tasks
              </Typography>
            </div>
          </div>
          <div className="dash-actions-grid">
            {QUICK_ACTIONS.map((action) => (
              <Link key={action.path} to={action.path} className="dash-action-tile">
                <span className="dash-action-icon">{action.icon}</span>
                <span className="dash-action-copy">
                  <Typography className="dash-action-label">{action.label}</Typography>
                  <Typography className="dash-action-desc">{action.description}</Typography>
                </span>
                <ArrowForwardIcon className="dash-action-arrow" />
              </Link>
            ))}
          </div>
        </section>

        <section className="section dash-card">
          <div className="dash-card-header">
            <div>
              <Typography className="dash-card-title">Subscription health</Typography>
              <Typography className="dash-card-subtitle">
                Live vs expired user subscriptions
              </Typography>
            </div>
            <Link to="/subscription-records">
              <Button size="small" endIcon={<ArrowForwardIcon />}>
                View all
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="dash-loading-block">
              <Skeleton variant="rounded" height={88} />
              <Skeleton variant="rounded" height={120} />
            </div>
          ) : (
            <>
              <div className="dash-metric-row">
                <div className="dash-metric-pill">
                  <Typography className="dash-metric-value success">
                    {subscriptionStats.active}
                  </Typography>
                  <Typography className="dash-metric-label">Active</Typography>
                </div>
                <div className="dash-metric-pill">
                  <Typography className="dash-metric-value muted">
                    {subscriptionStats.expired}
                  </Typography>
                  <Typography className="dash-metric-label">Expired</Typography>
                </div>
                <div className="dash-metric-pill highlight">
                  <Typography className="dash-metric-value">{activeRate}%</Typography>
                  <Typography className="dash-metric-label">Retention</Typography>
                </div>
              </div>

              <div className="dash-progress">
                <div className="dash-progress-labels">
                  <Typography className="dash-progress-text">
                    <TrendingUpIcon fontSize="inherit" /> Active share
                  </Typography>
                  <Typography className="dash-progress-text">
                    {subscriptionStats.active} of {subscriptionStats.total}
                  </Typography>
                </div>
                <div className="dash-progress-track">
                  <div
                    className="dash-progress-fill"
                    style={{ width: `${activeRate}%` }}
                  />
                </div>
              </div>

              <Typography className="dash-section-label">Recent payments</Typography>
              {recentRecords.length === 0 ? (
                <div className="dash-empty">
                  <MdReceipt className="dash-empty-icon" />
                  <Typography color="text.secondary">
                    No subscription payments recorded yet.
                  </Typography>
                </div>
              ) : (
                <div className="dash-list">
                  {recentRecords.map((record) => (
                    <div key={record._id} className="dash-list-item">
                      <div>
                        <Typography className="dash-list-title">
                          {record.fullName}
                        </Typography>
                        <Typography className="dash-list-meta">
                          {record.planName} · {formatDate(record.paymentDate)}
                        </Typography>
                      </div>
                      <Chip
                        size="small"
                        label={record.isLive ? "Active" : record.status}
                        color={record.isLive ? "success" : "default"}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        <section className="section dash-card dash-card-wide">
          <div className="dash-card-header">
            <div>
              <Typography className="dash-card-title">Recently updated vehicles</Typography>
              <Typography className="dash-card-subtitle">
                Latest changes across your catalogue
              </Typography>
            </div>
            <Link to="/vehicles">
              <Button size="small" endIcon={<ArrowForwardIcon />}>
                All vehicles
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="dash-loading-block">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={64} />
              ))}
            </div>
          ) : recentVehicles.length === 0 ? (
            <div className="dash-empty">
              <DirectionsCarFilledOutlinedIcon className="dash-empty-icon" />
              <Typography color="text.secondary" className="mb-2">
                No vehicles in the catalogue yet.
              </Typography>
              <Link to="/add_vehicles">
                <Button variant="contained" size="small" startIcon={<AddIcon />}>
                  Add first vehicle
                </Button>
              </Link>
            </div>
          ) : (
            <div className="dash-vehicle-list">
              {recentVehicles.map((vehicle) => {
                const discontinued = isDiscontinued(vehicle.status);
                return (
                  <Link
                    key={vehicle._id}
                    to={`/Update_Vehicle/${vehicle._id}`}
                    className={`dash-vehicle-row ${discontinued ? "is-discontinued" : ""}`}
                  >
                    {vehicle.vehicleImage ? (
                      <img
                        src={resolveImageUrl(vehicle.vehicleImage)}
                        alt=""
                        className="dash-vehicle-thumb"
                      />
                    ) : (
                      <div className="dash-vehicle-thumb-fallback">
                        <DirectionsCarFilledOutlinedIcon fontSize="small" />
                      </div>
                    )}
                    <div className="dash-vehicle-copy">
                      <Typography className="dash-vehicle-brand">
                        {vehicle.manufacturerType || "Unknown brand"}
                      </Typography>
                      <Typography className="dash-vehicle-name">
                        {vehicle.vehicle_name || "Unnamed vehicle"}
                      </Typography>
                      <Typography className="dash-vehicle-meta">
                        {vehicle.vehicleCategory || "Uncategorized"}
                        {vehicle.engineCC ? ` · ${vehicle.engineCC} cc` : ""}
                      </Typography>
                    </div>
                    <div className="dash-vehicle-side">
                      <Chip
                        size="small"
                        label={discontinued ? "Discontinued" : "Active"}
                        color={discontinued ? "default" : "success"}
                      />
                      <Typography className="dash-vehicle-date">
                        {formatDate(vehicle.lastUpdated || vehicle.created_at)}
                      </Typography>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Home;
